import { sortBy } from "lodash";
import API from "serviceshift-ui/api-client";

import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";
import { Quote } from "@/typings/quote";
import {
  getTaxabilityOptions,
  TaxabilityType,
  TaxSettingsContextTypes,
  TaxSettingsData,
  TaxSettingsDefaults
} from "@/typings/taxability";

const TaxSettingsActions = {
  /**
   * Get company tax settings and default form values based on the context of a customer,
   * address, quote, etc. In the future we may turn this into an API if we have lots
   * of logic going on.
   */
  async getDefaultTaxSettings(
    { dispatch },
    params: {
      isNew: boolean;
      context: any;
      contextType?: TaxSettingsContextTypes;
    }
  ) {
    const { isNew, context, contextType } = params;
    let formValues: TaxSettingsDefaults | undefined = undefined;
    const taxabilityOptions = getTaxabilityOptions(contextType);
    const companyDefaults: TaxSettingsDefaults = await dispatch(
      "getCurrentCompanyTaxDefaults"
    );
    if (contextType === "Customer") {
      if (isNew) {
        formValues = companyDefaults;
      } else {
        const customer = context as Customer | undefined;
        if (!customer?.tax_setting) {
          throw "InvalidContext(Customer): Missing customer tax settings";
        }
        formValues = TaxSettingsDefaults.fromSettings(customer.tax_setting);
      }
    } else if (contextType === "Address") {
      if (isNew) {
        const customerId = context as string | number;
        if (!customerId) throw "InvalidContext(Address): Missing customerId";
        const user: Customer = (
          await dispatch("fetchUser", {
            id: context,
            returnOnly: true
          })
        ).data;
        if (user.primary_address?.tax_setting) {
          formValues = TaxSettingsDefaults.fromSettings(
            user.primary_address.tax_setting
          );
        } else {
          // This case could happen because we have imported a customer and missing a primary address
          // We'll use the company defaults at this point.
          formValues = companyDefaults;
        }
      } else {
        const address = context as Address | undefined;
        if (!address?.tax_setting) {
          throw "InvalidContext(Address): Missing address tax settings";
        }
        formValues = TaxSettingsDefaults.fromSettings(address.tax_setting);
      }
    } else if (contextType === "Quote") {
      const quote = context as Quote & { tax_setting: TaxSettingsData | null };
      if (!quote?.tax_setting) {
        throw "InvalidContext(Quote): Missing quote tax settings";
      }
      formValues = TaxSettingsDefaults.fromSettings(quote.tax_setting);
    } else {
      throw `Invalid context type: ${contextType}`;
    }
    return { formValues, companyDefaults, taxabilityOptions };
  },
  async getCurrentCompanyTaxDefaults({ state, dispatch }) {
    if (!state.tenant) await dispatch("fetchTenant");
    return new TaxSettingsDefaults({
      tax_zone_id: state.tenant.default_tax_zone_id,
      taxability: state.tenant.default_taxability,
      reason: null
    });
  },
  async fetchCompanyTaxZones({ commit, state }) {
    const response = await API.makeRequest<{
      data: {
        id: string;
        name: string;
        description: string;
        tax_rate: number;
        formatted_name: string; // Ex: 2.76% - Tax Name
      }[];
    }>("/admin/v4/tax_zones");
    const companyTaxZones = response.data?.data || [];
    const sortedTaxZones = sortBy(companyTaxZones, (zone) => {
      return zone.name.toLowerCase();
    });
    commit("replace", {
      state: "taxability",
      data: {
        ...state.taxability,
        companyTaxZones: sortedTaxZones
      }
    });
  },
  async fetchCompanyTaxExemptionReasons({ commit, state }) {
    const response = await API.makeRequest<{
      data: {
        id: string;
        reason: string;
      }[];
    }>("/admin/v4/tax_exemption_reasons");
    const companyTaxExemptionReasons = response.data?.data || [];
    const sortedReasons = sortBy(companyTaxExemptionReasons, (reason) => {
      return reason.reason.toLowerCase();
    });
    commit("replace", {
      state: "taxability",
      data: {
        ...state.taxability,
        companyTaxExemptionReasons: sortedReasons
      }
    });
  },
  async updateTaxSettings(
    { dispatch },
    data: {
      id: number | null;
      configurable_gid: string;
      taxability: TaxabilityType;
      tax_zone_id: number;
      reason: string;
    }
  ) {
    await dispatch("confirmQuoteChange");
    if (data.id) {
      await API.makeRequest(`/admin/v4/tax_settings/${data.id}`, {
        method: "PUT",
        data
      });
    } else {
      await API.makeRequest(`/admin/v4/tax_settings`, {
        method: "POST",
        data
      });
    }
  }
};

export default TaxSettingsActions;
